import React from "react";

const DARK_NAV_ITEMS = [
  { key: "about", label: "About" },
  { key: "grounds", label: "Grounds" },
  { key: "facilities", label: "Facilities" },
  { key: "gallery", label: "Gallery" },
  { key: "location", label: "Location" },
];

const DarkTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  headline,
  subheadline,
  ctaLabel = "Book Now",
  openBooking,
  sectionRenderers = {},
  themeLayout = "dark",
  selectedThemeLayout = "dark",
}) => {
  const bookingEnabled = website?.booking?.enabled !== false;

  const visibleNavigation = DARK_NAV_ITEMS.filter(
    (item) => website?.sections?.[item.key] !== false
  );

  const orderedSections = Array.isArray(website?.sectionOrder)
    ? website.sectionOrder
        .filter((key) => key !== "hero")
        .filter((key) => website?.sections?.[key] !== false)
    : [];

  const city = venue?.location?.city || venue?.city || "Your city";
  const openingTime = venue?.policies?.openTime || "06:00";
  const closingTime = venue?.policies?.closeTime || "22:00";

  const sports = [
    ...new Set(
      grounds
        .map(
          (ground) =>
            ground?.sportName ||
            ground?.sport?.name ||
            ground?.sportType
        )
        .filter(Boolean)
    ),
  ];

  return (
    <main
      id="top"
      className={`pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle}`}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
      }}
    >
      <header className="pvd-header">
        <nav className="pvd-nav" aria-label="Venue website navigation">
          <a className="pvd-brand" href="#top">
            <span className="pvd-brand-mark" aria-hidden="true">
              <i />
            </span>

            <span className="pvd-brand-copy">
              <strong>{venue?.name || "Venue"}</strong>
              <small>{city}</small>
            </span>
          </a>

          <div className="pvd-nav-links">
            <a href="#top">Home</a>

            {visibleNavigation.map((item) => (
              <a href={`#${item.key}`} key={item.key}>
                {item.label}
              </a>
            ))}
          </div>

          {bookingEnabled && (
            <button
              type="button"
              className="pvd-nav-cta"
              onClick={() => openBooking?.()}
            >
              <span>{ctaLabel}</span>
              <strong aria-hidden="true">↗</strong>
            </button>
          )}
        </nav>
      </header>

      {website?.sections?.hero !== false && (
        <section className="pvd-hero">
          <div className="pvd-hero-glow pvd-hero-glow--one" />
          <div className="pvd-hero-glow pvd-hero-glow--two" />

          <div className="pvd-hero-copy">
            <div className="pvd-availability-label">
              <span />
              Reservations open
            </div>

            <h1>{headline}</h1>

            <p>{subheadline}</p>

            <div className="pvd-hero-actions">
              {bookingEnabled && (
                <button
                  type="button"
                  className="pvd-primary-action"
                  onClick={() => openBooking?.()}
                >
                  <span>{ctaLabel}</span>
                  <strong aria-hidden="true">→</strong>
                </button>
              )}

              {venue?.googleMapsUrl && (
                <a
                  className="pvd-secondary-action"
                  href={venue.googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Explore venue
                </a>
              )}
            </div>

            <div className="pvd-metrics">
              <div>
                <strong>{grounds.length}</strong>
                <span>Bookable grounds</span>
              </div>

              <div>
                <strong>{sports.length || 1}</strong>
                <span>Sports available</span>
              </div>

              <div>
                <strong>{openingTime}</strong>
                <span>Opening time</span>
              </div>
            </div>
          </div>

          <div className="pvd-hero-visual">
            <div className="pvd-image-frame">
              {heroImage ? (
                <img src={heroImage} alt={venue?.name || "Sports venue"} />
              ) : (
                <div className="pvd-placeholder">
                  <span>Experience</span>
                  <strong>{venue?.name || "Your venue"}</strong>
                </div>
              )}

              <div className="pvd-image-overlay" />

              <div className="pvd-image-title">
                <span>Featured venue</span>
                <strong>{venue?.name || "Sports venue"}</strong>
              </div>
            </div>

            <div className="pvd-floating-card pvd-floating-card--hours">
              <small>Available daily</small>
              <strong>
                {openingTime} — {closingTime}
              </strong>
            </div>

            <div className="pvd-floating-card pvd-floating-card--booking">
              <span className="pvd-live-dot" />
              <div>
                <strong>Online booking</strong>
                <small>Choose one or multiple slots</small>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="pvd-marquee" aria-label="Venue highlights">
        <div>
          <span>Reserve</span>
          <i />
          <span>Compete</span>
          <i />
          <span>Play</span>
          <i />
          <span>Return</span>
          <i />
          <span>Perform</span>
        </div>
      </section>

      <section className="pvd-service-grid">
        <article>
          <span>01</span>
          <div>
            <strong>Simple booking</strong>
            <p>Choose your ground, date and available time slots.</p>
          </div>
        </article>

        <article>
          <span>02</span>
          <div>
            <strong>Multiple slots</strong>
            <p>Add several available slots to the same reservation.</p>
          </div>
        </article>

        <article>
          <span>03</span>
          <div>
            <strong>Venue details</strong>
            <p>Review facilities, policies, location and pricing.</p>
          </div>
        </article>
      </section>

      <div className="pv-content pvd-content">
        {orderedSections.map((key) => {
          const renderSection = sectionRenderers[key];

          return renderSection ? (
            <React.Fragment key={key}>
              {renderSection()}
            </React.Fragment>
          ) : null;
        })}
      </div>

      {bookingEnabled && (
        <section className="pvd-final-cta">
          <div>
            <span>Your next session starts here</span>
            <h2>Reserve the perfect time for your next game.</h2>
          </div>

          <button type="button" onClick={() => openBooking?.()}>
            <span>{ctaLabel}</span>
            <strong aria-hidden="true">↗</strong>
          </button>
        </section>
      )}

      <footer className="pvd-footer">
        <div className="pvd-footer-top">
          <div className="pvd-footer-brand">
            <span className="pvd-brand-mark" aria-hidden="true">
              <i />
            </span>

            <div>
              <strong>{venue?.name || "Venue"}</strong>
              <small>{city}</small>
            </div>
          </div>

          <div className="pvd-footer-hours">
            <span>Opening hours</span>
            <strong>
              {openingTime} — {closingTime}
            </strong>
          </div>
        </div>

        <p>
          Browse available grounds, review venue information and reserve your
          preferred sports slots online.
        </p>

        <div className="pvd-footer-bottom">
          <span>
            © {new Date().getFullYear()} {venue?.name || "Venue"}
          </span>
          <span>Powered by OnGround</span>
        </div>
      </footer>
    </main>
  );
};

export default DarkTheme;